import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import Logo from '../assets/img/logo.svg';
import { FaUser } from 'react-icons/fa';

const Navigation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [showNav, setShowNav] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const isDetailPage =
      location.pathname.includes('/news/') ||
      location.pathname.includes('/events/') ||
      location.pathname.includes('/staff') ||
      location.pathname.includes('/sponsors') ||
      location.pathname.includes('/support') ||
      location.pathname.includes('/privacy') ||
      location.pathname === '/news' ||
      location.pathname === '/events';

    const updateNav = () => {
      setShowNav(window.scrollY > 200 || isDetailPage);
    };

    updateNav();
    window.addEventListener('scroll', updateNav, { passive: true });
    return () => window.removeEventListener('scroll', updateNav);
  }, [location.pathname]);

  const scrollToPartners = () => {
    setMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollToPartners: true } });
    } else {
      document.getElementById('partners')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navElement = (
    <header
      className={`modern-header ${showNav ? 'scrolled visible' : 'hidden'}`}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, width: '100%',
        zIndex: 999999,
        background: 'rgba(0, 35, 54, 0.98)',
        transform: showNav ? 'translateY(0)' : 'translateY(-100%)',
        opacity: showNav ? 1 : 0,
        transition: 'all 0.4s ease',
        borderBottom: showNav ? '2px solid #00cbcd' : 'none',
        backdropFilter: 'blur(20px)',
      }}
    >
      <nav className="navbar navbar-expand-lg">
        <div className="container">
          <Link className="navbar-brand" to="/" onClick={() => setMenuOpen(false)}>
            <img
              src={Logo}
              alt="Mileage Riot Logo"
              onError={e => { e.target.src = 'https://hub.mileageriot.com/assets/images/logo_sm.png'; }}
            />
          </Link>

          {/* Hamburger — React controlled */}
          <button
            className="navbar-toggler"
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          {/* Menu */}
          <div className={`navbar-collapse ${menuOpen ? 'show' : 'collapse'}`} id="navbarSupportedContent">
            <ul className="navbar-nav mx-auto menu">
              <li className="nav-item">
                <NavLink className="nav-link" to="/" end onClick={() => setMenuOpen(false)}>
                  {t('nav.home')}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/news" onClick={() => setMenuOpen(false)}>
                  {t('nav.news')}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/staff" onClick={() => setMenuOpen(false)}>
                  {t('nav.team')}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/events" onClick={() => setMenuOpen(false)}>
                  {t('nav.convoys')}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/invite-convoy" onClick={() => setMenuOpen(false)}>
                  {t('nav.inviteConvoy')}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/sponsors" onClick={() => setMenuOpen(false)}>
                  {t('nav.sponsors')}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/support" onClick={() => setMenuOpen(false)}>
                  {t('nav.support')}
                </NavLink>

              </li>
              <li className="nav-item">
                <button className="nav-link nav-button" onClick={scrollToPartners}>
                  {t('nav.partners')}
                </button>
              </li>
              <li className="nav-item d-md-none d-block">
                <NavLink className="nav-link" to="https://hub.mileageriot.com/dashboard" onClick={() => setMenuOpen(false)}>
                  {t('nav.login')}
                </NavLink>
              </li>
            </ul>

            <div className="d-flex align-items-center">
              <LanguageSwitcher />
              <div className="ms-3 d-none d-md-block">
                <Link to="https://hub.mileageriot.com/dashboard" className="btn btn-join" type="button">
                  <FaUser className="me-1" /> {t('nav.login')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );

  return createPortal(navElement, document.body);
};

export default Navigation;
